import { Component, Input, OnDestroy, OnInit, SimpleChanges, ViewChild, } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { CENTER_DB_MAPPINGS } from '@shared/db-fields-mappings/center-db.mappings';
import { SearchArea } from '@shared/enums/SearchArea';
import { TCategory } from '@shared/interfaces/TCategory';
import { TCenter } from '@shared/interfaces/TCenter';
import { TImportMappingItem } from '@shared/interfaces/TImportMappingItem';
import { TProject } from '@shared/interfaces/TProject';
import { TSearchParams } from '@shared/interfaces/TSearchParams';
import { UserLevel } from '@shared/interfaces/TUser';
import { isNullOrUndefined } from '@shared/utils/isNullOrUndefined';
import { Subscription } from 'rxjs';
import { TDataColumnDef, buildColumnsDef } from 'src/utils/buildColumnsDef';
import { ROUTES_PATHS } from '../../../app-routing.module';
import { CommonTableComponent } from '../../../components/common-table/common-table.component';
import { ICommonTableParams, IHandleCommonTable, SortType, TCenterTableRow, TTDataType, TTableRowType, TableRowType, } from '../../../components/common-table/common-table.component.types';
import { TableDataExtender } from '../../../components/common-table/utils/TableDataExtender';
import { TCenterParams, TComponentType, } from '../../../interfaces/component-params.interface';
import { TPagingItem } from '../../../interfaces/paging-item.interface';
import { AuthService, TCurrentUser } from '../../../services/auth.service';
import { CentersService } from '../../../services/centers.service';
import { PagingService } from '../../../services/paging.service';
import { RegionsService, TRegion, WORLD_REGION, } from '../../../services/regions.service';
import { NEW_CENTER_ID } from '../center/center.component';

@Component({
  selector: 'app-centers-review',
  templateUrl: './centers-review.component.html',
  styleUrls: ['./centers-review.component.scss'],
})
export class CentersReviewComponent implements OnInit, IHandleCommonTable, OnDestroy {

  private readonly debug: boolean = false;

  @Input() public selectedCategory: TCategory | undefined;
  @Input() public selectedProject: TProject | undefined;
  @Input() public onImportFinish: () => void = () => void 0;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(CommonTableComponent) table: CommonTableComponent;

  public dataSource: MatTableDataSource<TCenterTableRow>;
  public centers: TCenter[] = [];
  public centersLoaded: boolean = false;
  public centersCount: number = 0;
    public columns: TDataColumnDef<TCenter>[] = [
    ...buildColumnsDef({
      textColumns: [],
      fieldsMappings: Array.from(CENTER_DB_MAPPINGS.values()).filter(
        (f) => f.visibleInSummary
      ),
    }),
  ];
  public tableParams: ICommonTableParams<TTableRowType, TTDataType>;
  private subscriptions: Subscription[] = [];
  private _countries: TRegion[] = [];
  private searchTerm: string = '';

  constructor(
    private _centersService: CentersService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _pagingService: PagingService,
    private _regionsService: RegionsService,
    private _authService: AuthService
  ) {
    this.dataSource = new MatTableDataSource();
    this.subscriptions.push(
      this._centersService.getCentersObservable().subscribe((centers: TCenter[]) => {
        this.centers = centers;
        this._processTableData();
      })
    );
    this.subscriptions.push(
      this._centersService.getCentersCountObservable().subscribe((centersCount) => {
        this.centersCount = centersCount;
      })
    );
    this.subscriptions.push(
      this._regionsService.getSelectedCountriesObservable().subscribe((countries) => {
        if (countries.filter((c) => c.value === WORLD_REGION.value).length === 0) {
          this._onRegionChange(countries);
        } else {
          this._onRegionChange([]);
        }
      })
    );
  }

  public ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  public ngOnInit(): void {
    this.centersLoaded = true;
    this._setTableData();

    if (this.selectedProject === undefined) {
      this._router.navigate([`/${ROUTES_PATHS.root}`]);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.tableParams = this.getTableParams();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public paginationCallback(e: PageEvent): void {
    if (this.selectedProject !== undefined) {
      const currentPaging: TPagingItem = structuredClone(
        this._pagingService.getPagingItem(this.selectedProject.id)
      );
      currentPaging.centersPaging.pageIndex = e.pageIndex;
      currentPaging.centersPaging.pageSize = e.pageSize;
      this._pagingService.updatePagingItem(this.selectedProject.id, currentPaging);
      this._setTableData();
    }
  }

  public applyFilter(term: string) {
    this.searchTerm = term;
    if (term.length >= 3) {
      this._setTableData(this.searchTerm);
      this.dataSource?.paginator?.firstPage();
    } else if (term.length === 0) {
      this._setTableData();
      this.searchTerm = '';
      this.dataSource?.paginator?.firstPage();
    }
  }

  public isDataColumn<T>(column: TDataColumnDef<T>): boolean {
    return column.isDataCell === true;
  }

  public getTableParams(): ICommonTableParams<TTableRowType, TTDataType> {
    if (this.debug) console.log('# CentersReviewComponent getTableParams selectedProject', this.selectedProject);
    const currentPaging: TPagingItem = this._pagingService.getPagingItem(this.selectedProject?.id ?? '');
    if (this.debug) console.log('# CentersReviewComponent getTableParams currentPaging', currentPaging);
    const result = {
      columns: this.columns as TDataColumnDef<TTDataType>[],
      dataCount: currentPaging?.centersPaging?.total || 0, // TODO ERROR in Fotivda
      dataLoaded: this.centersLoaded,
      dataSource: this.dataSource as MatTableDataSource<TTableRowType>,
      displayedColumns: Array.from(CENTER_DB_MAPPINGS.values()).filter((f) => f.visibleInSummary).map((f) => f.fieldId),
      handleEditClick: (row: TTableRowType) => this._handleEditClick(<TCenterTableRow>row),
      pageIndex: currentPaging?.centersPaging?.pageIndex,
      pageSize: currentPaging?.centersPaging?.pageSize,
      pageSizeOptions: this._pagingService.pageSizeOptions,
      paginationCallback: (e: PageEvent) => this.paginationCallback(e),
      downloadCallback: () => this.downloadCenters(),
      importCallback: (e: Event, mappings: TImportMappingItem[]) => this.importCenters(e, mappings),
      area: SearchArea.Centers,
      projectId: this.selectedProject?.id ?? '',
      filterCallback: (term: string) => this.applyFilter(term),
      addNewParams: {
        addNewCallback: () => this._handleAddNewCenter(),
        tooltip: 'Add new center',
      },
      handleHeaderClick: (fieldId: string, remove: boolean) => this._handleHeaderClick(fieldId, remove),
      resize: true,
      selection: true,
      actionsAvailable: true,
      sortType: SortType.Extended,
      resetTable: () => this._setTableData(),
    };
    if (this.debug) console.log('# CentersReviewComponent getTableParams result', result);
    if (this.debug) console.log('---');
    return result;
  }

  private _handleHeaderClick(fieldId: string, remove: boolean): void {
    if (this.selectedProject !== undefined) {
      this._pagingService.handleSort({
        area: SearchArea.Centers,
        fieldId: fieldId,
        projectId: this.selectedProject.id,
        remove: remove,
      });
      this._setTableData();
    }
  }

  private _handleAddNewCenter(): void {
    if (this.selectedCategory !== undefined && this.selectedProject !== undefined) {
      const centerParams: TCenterParams = {
        centerId: NEW_CENTER_ID,
        categoryId: this.selectedCategory.categoryId,
        projectId: this.selectedProject.id,
        __type: TComponentType.center,
      };
      this._router.navigate([`/${ROUTES_PATHS.center}`, centerParams], {
        relativeTo: this._route,
      });
    }
  }

  private importCenters(e: Event, mappings: TImportMappingItem[]): void {
    if (this.selectedCategory !== undefined && this.selectedProject !== undefined) {
      this._centersService.importCenters(e, () => this.onImportFinish(), this.selectedProject.id, mappings);
    }
  }

  private downloadCenters(): void {
    if (this.selectedCategory !== undefined && this.selectedProject !== undefined) {
      this._centersService.downloadCenters(
        this.selectedCategory.categoryId,
        this.selectedProject.id
      );
    }
  }

  private _handleEditClick(row: TCenterTableRow): void {
    if (this.selectedCategory !== undefined && this.selectedProject !== undefined) {
      const centerParams: TCenterParams = {
        centerId: row.c_id,
        categoryId: this.selectedCategory.categoryId,
        projectId: this.selectedProject.id,
        __type: TComponentType.center,
      };
      this._router.navigate([`/${ROUTES_PATHS.center}`, centerParams], { relativeTo: this._route });
    }
  }

  private _setTableData(query: string = ''): void {
    if (this.selectedProject !== undefined && this.selectedCategory !== undefined) {
      const currentPaging: TPagingItem = this._pagingService.getPagingItem(
        this.selectedProject.id
      );
      if (isNullOrUndefined(query) && !isNullOrUndefined(this.table?.currentQuery)) {
        query = this.table.currentQuery;
      }
      if (!isNullOrUndefined(currentPaging?.centersPaging?.query)) {
        query = currentPaging?.centersPaging?.query || '';
      }
      const currentUser: TCurrentUser = this._authService.getCurrentUser();
      const isAdmin = currentUser?.level === UserLevel.Admin;
      const showAllCountries = isAdmin || (currentUser?.countries || []).length === 0;

      const params: TSearchParams<TCenter> = {
        area: SearchArea.Centers,
        paging: currentPaging?.centersPaging,
        projectId: this.selectedProject.id,
        query: query,
        results: [],
        countries: this._countries.map((c) => c.value),
        showAllCountries,
      };
      this._centersService.searchAndSetCentersUpdatePaging$(params).subscribe(res => {
        this.table.searchModel = query;
      });
    }
    this._processTableData();
  }

  private _processTableData(): void {
    if (!isNullOrUndefined(this.selectedProject)) {
      const dataColumns: TCenterTableRow[] = this.centers.map(
        (center, index) => {
          const extendedCenterProps: any = TableDataExtender.CenterTableDataExtender(center, this.selectedProject!.id);
          return {
            ...extendedCenterProps,
            ...center,
            position: index + 1,
            __type: TableRowType.Center,
          };
        }
      );
      this.dataSource.data = dataColumns;
    }
  }

  private _onRegionChange(countries: TRegion[]): void {
    this._countries = countries;
    if (this.selectedProject !== undefined) {
      const currentPaging: TPagingItem = this._pagingService.getPagingItem(this.selectedProject.id);
      currentPaging.centersPaging.pageIndex = 0;
      this._pagingService.updatePagingItem(this.selectedProject.id, currentPaging);
    }
    this._setTableData(this.searchTerm);
  }
}
